import { Injectable, EventEmitter } from "@angular/core";
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { map, distinctUntilChanged, tap, catchError } from "rxjs/operators";

import { BrokerService } from "./broker.service";

interface HttpServiceRequestOptions {
  method?: string;
  headers?: HttpHeaders;
  payload?: any;
  searchParams?: any;
  stringifyPayload?: boolean;
}

interface HttpServiceRequest {
  request: Request;
  done?: boolean;
}

@Injectable()
export class HttpService {
  private pendingRequestCountChanged: EventEmitter<number>;
  public hasPendingRequests: Observable<boolean>;
  private pendingRequestCount: number;

  constructor(private http: HttpClient, private broker: BrokerService) {
    this.pendingRequestCount = 0;
    this.pendingRequestCountChanged = new EventEmitter<number>();
    this.hasPendingRequests = this.pendingRequestCountChanged.pipe(
      map((count) => count > 0),
      distinctUntilChanged()
    );
  }

  exportList(url: string, options: HttpServiceRequestOptions = { method: "GET" }) {
    return this.http.get(url, { params: options.searchParams, responseType: "blob" });
  }

  downloadFile(url: string) {
    return this.http.get(url, { responseType: "blob" });
  }

  downloadText(url: string, options: HttpServiceRequestOptions = { method: "GET" }) {
    let method = options.method,
      body =
        options.payload && options.stringifyPayload !== false
          ? JSON.stringify(options.payload)
          : options.payload,
      headers =
        options.headers || new HttpHeaders({ "Content-Type": "application/json; charset=UTF-8" }),
      params = options.searchParams && this.buildHttpParams(options.searchParams);

    this.addXsrfToken(headers);

    return new Observable((subscriber: any) => {
      this.requestStarted(url);
      let req = this.http.get(url, { headers, params, responseType: "text" });
      return req
        .pipe(
          tap(() => {
            this.requestFinished(url);
          }),
          catchError((error) => {
            return this.handleError(error);
          })
        )
        .subscribe(subscriber);
    });
  }

  request(url: string, options: HttpServiceRequestOptions = { method: "GET" }): Observable<any> {
    options = options || {};

    let method = options.method,
      body =
        options.payload && options.stringifyPayload !== false
          ? JSON.stringify(options.payload)
          : options.payload,
      headers =
        options.headers || new HttpHeaders({ "Content-Type": "application/json; charset=UTF-8" }),
      params = options.searchParams && this.buildHttpParams(options.searchParams);

    this.addXsrfToken(headers);

    let req;
    return new Observable((subscriber: any) => {
      this.requestStarted(url);

      switch (method) {
        case "POST":
          req = this.http.post<any>(url, body, { headers, params });
          break;
        case "DELETE":
          req = this.http.delete<any>(url, { headers, params });
          break;
        case "PUT":
          req = this.http.put<any>(url, body, { headers, params });
          break;
        default:
          req = this.http.get<any>(url, { headers, params });
      }

      return req
        ? req
            .pipe(
              tap((response) => {
                this.requestFinished(url);
              }),
              catchError((error) => {
                return this.handleError(error);
              })
            )
            .subscribe(subscriber)
        : null;
    });
  }

  private requestStarted(url: string) {
    this.broker.broadcast("http.service.request.started", url);
    setTimeout(() => {
      this.pendingRequestCountChanged.emit(++this.pendingRequestCount);
    });
  }

  private requestFinished(url: string, response?: HttpResponse<any>) {
    if (response && response.headers) {
      this.broker.broadcast("http.service.app.version", response.headers.get("App-Version"));
    }
    this.broker.broadcast("http.service.request.completed", url);
    setTimeout(() => {
      this.pendingRequestCountChanged.emit(--this.pendingRequestCount);
    });
  }

  private addXsrfToken(headers: HttpHeaders): void {
    let matches = document.cookie.match(/\bXSRF-TOKEN=([^\s;]+)/);
    let xsrfToken = matches && decodeURIComponent(matches[1]);
    if (xsrfToken) {
      headers["X-XSRF-TOKEN"] = xsrfToken;
    }
  }

  buildHttpParams(filters: { [key: string]: any }): HttpParams {
    let searchParams = new HttpParams();

    Object.keys(filters).forEach((filterName) => {
      let filterValue = filters[filterName];
      if (filterValue) searchParams = searchParams.set(filterName, filterValue);
    });

    let dynamicRandomValue = new Date().getTime().toString();
    searchParams = searchParams.append("v", dynamicRandomValue);

    return searchParams;
  }

  handleError(error: HttpErrorResponse) {
    this.requestFinished(error.url);
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error("An ErrorEvent error occurred:", error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong.
      error.toString = function () {
        return this.status === 0
          ? "Unreachable server. Please check network connection and try again."
          : `(${this.status}) ${this.statusText}. ${this.error ? this.error : ""}`;
      };
      console.error(error);
    }
    // Return an observable with a user-facing error message.
    return throwError(error);
  }
}
